import { useState } from "react";
import { fileUrl } from "@/lib/api";
import { Phone, Mail, Instagram, Facebook, Linkedin, Globe } from "lucide-react";
import { getSurfaceStyle, getBlockRadius, getButtonRadius } from "@/lib/templateSets";
import { marked } from "marked";
import DOMPurify from "dompurify";

// Configure marked: GitHub-flavoured, line-break on single newline
marked.setOptions({ gfm: true, breaks: true });

/**
 * Render rich text:
 *  - If the string contains any Markdown / HTML-ish syntax → parse with marked + sanitise with DOMPurify
 *  - Otherwise → preserve as-is with whitespace pre-wrap (so plain newlines still render).
 * This means existing plain-text blocks keep their newlines AND admins can opt into
 * **bold**, *italic*, [links](url), lists, headings (#, ##), etc.
 */
function renderRichText(text) {
  if (!text) return { __html: "" };
  const html = DOMPurify.sanitize(marked.parse(text), {
    ALLOWED_TAGS: ["p","br","strong","em","u","s","a","ul","ol","li","h1","h2","h3","h4","blockquote","code","pre","hr","span"],
    ALLOWED_ATTR: ["href","target","rel","title","class"],
  });
  return { __html: html };
}

export const COMPONENT_TYPES = [
  { type: "heading", label: "Überschrift" },
  { type: "text", label: "Textblock" },
  { type: "image", label: "Bild" },
  { type: "image_fullwidth", label: "Bild Full-Width" },
  { type: "button", label: "Button" },
  { type: "hero", label: "Hero" },
  { type: "team", label: "Team" },
  { type: "slider", label: "Slider" },
  { type: "testimonials", label: "Testimonials" },
  { type: "faq", label: "FAQ" },
  { type: "spacer", label: "Abstand" },
  { type: "form", label: "Formular" },
  { type: "booking", label: "Buchung" },
  { type: "newsletter", label: "Newsletter" },
  { type: "loyalty", label: "Treueprogramm" },
  { type: "pricelist", label: "Preisliste" },
];

export const DEFAULTS = {
  heading: { text: "Neue Überschrift", level: "h2", align: "left" },
  text: { text: "Hier steht ein neuer Textblock. Klicken Sie zum Bearbeiten.", align: "left" },
  image: { src: "https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=1974", alt: "", caption: "" },
  image_fullwidth: {
    src: "https://images.unsplash.com/photo-1521590832167-7bfc17484d20?q=80&w=2400",
    alt: "",
    overlay_title: "",
    overlay_subtitle: "",
    height: 480,
    overlay_opacity: 0.35,
    text_align: "center",
  },
  button: { label: "Mehr erfahren", url: "#", variant: "primary" },
  hero: {
    title: "Willkommen",
    subtitle: "Untertitel",
    cta_label: "Termin buchen",
    cta_url: "#",
    image: "https://images.unsplash.com/photo-1521590832167-7bfc17484d20?q=80&w=2070",
    layout: "background",
    overlay_opacity: 0.35,
  },
  team: {
    title: "Unser Team",
    subtitle: "Die Menschen, die dich begleiten.",
    staff_ids: [],            // empty = show all active staff
    columns: 3,               // 2 | 3 | 4
    show_bio: true,
    show_contact: true,
    show_social: true,
    card_style: "elevated",   // elevated | flat | bordered | overlay
  },
  slider: {
    images: [
      "https://images.unsplash.com/photo-1560066984-138dadb4c035?q=80&w=1974",
      "https://images.unsplash.com/photo-1599305090598-fe179d501227?q=80&w=2000",
      "https://images.unsplash.com/photo-1620331311520-246422fd82f9?q=80&w=1974",
    ],
  },
  testimonials: {
    items: [
      { quote: "Toller Salon!", author: "Kundin A" },
      { quote: "Sehr empfehlenswert.", author: "Kunde B" },
    ],
  },
  faq: { items: [{ question: "Frage?", answer: "Antwort." }], columns: 1 },
  spacer: { height: 48 },
  form: { form_id: "" },
  booking: { title: "Termin online buchen", subtitle: "Wählen Sie Leistung und Wunschzeit" },
  newsletter: { title: "Bleiben Sie informiert", subtitle: "Erhalten Sie Trends, Aktionen und neue Termine direkt in Ihr Postfach." },
  loyalty: {
    title: "Sammeln Sie Treuepunkte",
    subtitle: "Bei jedem Besuch Punkte sammeln und gegen exklusive Belohnungen einlösen.",
    cta_label: "Jetzt Konto erstellen",
    max_rewards: 3,
  },
  pricelist: {
    title: "Unsere Preise",
    subtitle: "",
    group_by_category: true,
    show_uncategorized: true,
    show_description: true,
    show_duration: true,
    show_addons: true,
    layout: "list",           // list | cards
    category_ids: [],         // empty = show all categories
    cta_label: "Termin online buchen",
    show_cta: true,
  },
};

function getAlign(a) {
  return a === "center" ? "text-center" : a === "right" ? "text-right" : "text-left";
}

export function BlockRenderer({ block, theme = {}, forms = [], services = [], staff = [], addons = [], categories = [], planFeatures = null, salonSlug, loyalty = null, onSubmitForm }) {
  const { type, props } = block;
  const headingFont = theme.heading_font || "Playfair Display";
  const bodyFont = theme.body_font || "Manrope";
  const primary = theme.primary_color || "#C87A5F";
  const blockRadius = getBlockRadius(theme);
  const cardSurface = getSurfaceStyle(theme);
  const buttonRadius = getButtonRadius(theme);
  // planFeatures === null → preview/admin context, don't restrict (see "newsletter" below).
  const bookingHref = `/salon/${salonSlug}/booking`;
  const bookingAllowed = !Array.isArray(planFeatures) || planFeatures.includes("bookings");

  if (type === "heading") {
    const Tag = props.level || "h2";
    const sizes = { h1: "text-4xl md:text-6xl", h2: "text-3xl md:text-5xl", h3: "text-2xl md:text-4xl", h4: "text-xl md:text-2xl" };
    // props.font (per-heading override) > theme.heading_font (platform/salon default) > fallback
    const font = props.font || headingFont;
    return (
      <Tag style={{ fontFamily: `'${font}', serif` }} className={`${sizes[Tag]} ${getAlign(props.align)} leading-tight my-4`}>
        {props.text}
      </Tag>
    );
  }
  if (type === "text") {
    return (
      <div
        className={`${getAlign(props.align)} text-base md:text-lg leading-relaxed my-3 max-w-3xl mv-prose ${props.align === "center" ? "mx-auto" : ""}`}
        style={{ fontFamily: `'${bodyFont}', sans-serif`, whiteSpace: "pre-wrap" }}
        dangerouslySetInnerHTML={renderRichText(props.text)}
      />
    );
  }
  if (type === "image") {
    return (
      <figure className="my-6">
        <img src={fileUrl(props.src)} alt={props.alt || ""} className="w-full object-cover max-h-[600px]" style={{ borderRadius: blockRadius }} />
        {props.caption && <figcaption className="text-sm mt-2 text-center" style={{ color: "var(--mv-text-muted)" }}>{props.caption}</figcaption>}
      </figure>
    );
  }
  if (type === "image_fullwidth") {
    const h = props.height || 480;
    const op = typeof props.overlay_opacity === "number" ? props.overlay_opacity : 0.35;
    const align = props.text_align === "left" ? "flex-start" : props.text_align === "right" ? "flex-end" : "center";
    const textAlign = props.text_align === "left" ? "left" : props.text_align === "right" ? "right" : "center";
    return (
      <section
        className="relative w-screen overflow-hidden"
        style={{ height: `${h}px`, marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
      >
        <img src={fileUrl(props.src)} alt={props.alt || ""} className="absolute inset-0 w-full h-full object-cover" />
        {(props.overlay_title || props.overlay_subtitle) && (
          <div aria-hidden className="absolute inset-0" style={{ background: `rgba(0,0,0,${op})` }} />
        )}
        {(props.overlay_title || props.overlay_subtitle) && (
          <div className="relative h-full flex flex-col justify-center px-6 lg:px-16" style={{ alignItems: align, textAlign }}>
            {props.overlay_title && (
              <h2 style={{ fontFamily: `'${headingFont}', serif`, color: "#fff", fontSize: "clamp(28px,4.5vw,56px)", lineHeight: 1.1, fontWeight: 700, letterSpacing: -1, maxWidth: 880 }}>
                {props.overlay_title}
              </h2>
            )}
            {props.overlay_subtitle && (
              <p className="mt-3" style={{ color: "rgba(255,255,255,0.92)", fontSize: 18, maxWidth: 720 }}>
                {props.overlay_subtitle}
              </p>
            )}
          </div>
        )}
      </section>
    );
  }
  if (type === "button") {
    return (
      <div className="my-4">
        <a href={props.url} className="inline-flex items-center px-6 py-3 text-white font-medium" style={{ background: primary, borderRadius: buttonRadius }}>
          {props.label}
        </a>
      </div>
    );
  }
  if (type === "hero") {
    const heroLayout = props.layout || "background";
    const overlayOp = typeof props.overlay_opacity === "number" ? props.overlay_opacity : 0.35;
    const hasImg = !!props.image;

    // Split layout: image right, text left (light surface, fits inside container)
    if (heroLayout === "split") {
      return (
        <section className="my-6 px-4 md:px-8 py-12 md:py-20" style={{ borderRadius: blockRadius, background: "var(--mv-surface, #faf8f5)" }}>
          <div className="grid md:grid-cols-2 gap-10 items-center max-w-6xl mx-auto">
            <div>
              <h1 style={{ fontFamily: `'${headingFont}', serif` }} className="text-4xl md:text-5xl lg:text-6xl leading-tight mb-4">{props.title}</h1>
              <p className="text-base md:text-lg mb-8" style={{ color: "var(--mv-text-muted)" }}>{props.subtitle}</p>
              {props.cta_label && (bookingAllowed || props.cta_url !== bookingHref) && (
                <a href={props.cta_url} className="inline-flex items-center px-7 py-3.5 font-medium" style={{ background: primary, color: "#fff", borderRadius: buttonRadius }}>
                  {props.cta_label}
                </a>
              )}
            </div>
            {hasImg && (
              <div className="relative">
                <img src={fileUrl(props.image)} alt="" className="w-full h-[420px] object-cover shadow-xl" style={{ borderRadius: blockRadius }} />
              </div>
            )}
          </div>
        </section>
      );
    }

    // background / fullscreen: break out of the parent's max-w-6xl + horizontal padding
    // so the hero can span the FULL viewport width.
    const isFs = heroLayout === "fullscreen";
    const breakOut = {
      position: "relative",
      width: "100vw",
      left: "50%",
      right: "50%",
      marginLeft: "-50vw",
      marginRight: "-50vw",
    };
    return (
      <section
        className="relative flex items-center justify-center overflow-hidden"
        style={{
          ...breakOut,
          minHeight: isFs ? "100vh" : "min(640px, 70vh)",
          marginTop: isFs ? "-32px" : 24,
          marginBottom: isFs ? 32 : 24,
        }}
      >
        {hasImg && <img src={fileUrl(props.image)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(0,0,0,${overlayOp * 0.7}) 0%, rgba(0,0,0,${overlayOp}) 100%)` }} />
        <div className="relative z-10 text-center text-white p-8 max-w-3xl">
          <h1 style={{ fontFamily: `'${headingFont}', serif`, textShadow: "0 2px 24px rgba(0,0,0,0.35)" }} className="text-4xl md:text-6xl lg:text-7xl leading-tight mb-4">{props.title}</h1>
          <p className="text-base md:text-xl mb-8 opacity-95">{props.subtitle}</p>
          {props.cta_label && (bookingAllowed || props.cta_url !== bookingHref) && (
            <a href={props.cta_url} className="inline-flex items-center px-7 py-3.5 font-medium" style={{ background: primary, color: "#fff", borderRadius: buttonRadius }}>
              {props.cta_label}
            </a>
          )}
        </div>
      </section>
    );
  }
  if (type === "team") {
    return <TeamBlock props={props} staff={staff} theme={theme} headingFont={headingFont} bodyFont={bodyFont} primary={primary} cardSurface={cardSurface} blockRadius={blockRadius} />;
  }
  if (type === "slider") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
        {(props.images || []).map((src, i) => (
          <img key={i} src={fileUrl(src)} alt="" className="w-full h-72 object-cover" style={{ borderRadius: blockRadius }} />
        ))}
      </div>
    );
  }
  if (type === "testimonials") {
    return (
      <div className="grid md:grid-cols-2 gap-5 my-8">
        {(props.items || []).map((it, i) => (
          <div key={i} className="p-6" style={cardSurface}>
            <p style={{ fontFamily: `'${headingFont}', serif` }} className="text-xl italic leading-relaxed mb-3">&ldquo;{it.quote}&rdquo;</p>
            <p className="text-sm" style={{ color: "var(--mv-text-muted)" }}>— {it.author}</p>
          </div>
        ))}
      </div>
    );
  }
  if (type === "faq") {
    const cols = props.columns === 2 ? "md:grid-cols-2" : "grid-cols-1";
    return (
      <div className={`my-6 grid ${cols} gap-3 auto-rows-start`}>
        {(props.items || []).map((it, i) => (
          <details key={i} className="p-5" style={cardSurface}>
            <summary className="cursor-pointer font-medium">{it.question}</summary>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--mv-text-muted)" }}>{it.answer}</p>
          </details>
        ))}
      </div>
    );
  }
  if (type === "spacer") return <div style={{ height: props.height || 32 }} />;
  if (type === "form") {
    const f = forms.find((ff) => ff.id === props.form_id) || forms[0];
    if (!f) return <p className="text-sm italic" style={{ color: "var(--mv-text-muted)" }}>Kein Formular ausgewählt</p>;
    return <PublicForm form={f} primary={primary} buttonRadius={buttonRadius} cardSurface={cardSurface} onSubmit={onSubmitForm} />;
  }
  if (type === "booking") {
    // Hide on public salon pages when the plan doesn't include online booking.
    // planFeatures === null → preview/admin context → render anyway.
    if (Array.isArray(planFeatures) && !bookingAllowed) return null;
    return <BookingBlock title={props.title} subtitle={props.subtitle} headingFont={headingFont} primary={primary} services={services} salonSlug={salonSlug} cardSurface={cardSurface} buttonRadius={buttonRadius} blockRadius={blockRadius} />;
  }
  if (type === "newsletter") {
    // Hide on public salon pages when the salon's plan does not include the
    // newsletter module. `planFeatures === null` = preview/admin context →
    // render anyway so admins can still edit the block.
    if (Array.isArray(planFeatures) && !planFeatures.includes("newsletter")) return null;
    return <NewsletterBlock title={props.title} subtitle={props.subtitle} headingFont={headingFont} primary={primary} salonSlug={salonSlug} blockRadius={blockRadius} buttonRadius={buttonRadius} />;
  }
  if (type === "loyalty") {
    return <LoyaltyBlock title={props.title} subtitle={props.subtitle} ctaLabel={props.cta_label} maxRewards={props.max_rewards || 3} headingFont={headingFont} primary={primary} salonSlug={salonSlug} loyalty={loyalty} blockRadius={blockRadius} buttonRadius={buttonRadius} />;
  }
  if (type === "pricelist") {
    return <PricelistBlock props={props} services={services} addons={addons} categories={categories} headingFont={headingFont} bodyFont={bodyFont} primary={primary} salonSlug={salonSlug} cardSurface={cardSurface} blockRadius={blockRadius} buttonRadius={buttonRadius} bookingAllowed={bookingAllowed} />;
  }
  return <div className="text-xs text-red-500">Unbekannter Block: {type}</div>;
}

function TeamBlock({ props, staff, theme, headingFont, bodyFont, primary, cardSurface, blockRadius }) {
  const filteredStaff = (staff || []).filter((s) => s.active !== false);
  const list = (props.staff_ids && props.staff_ids.length > 0)
    ? props.staff_ids.map((id) => filteredStaff.find((s) => s.id === id)).filter(Boolean)
    : filteredStaff;
  if (list.length === 0) {
    return (
      <section className="my-10 text-center text-sm italic" style={{ color: "var(--mv-text-muted)" }}>
        Noch keine Team-Mitglieder hinzugefügt — verwalte sie unter &bdquo;Team&ldquo;.
      </section>
    );
  }
  const cols = props.columns === 2 ? "md:grid-cols-2" : props.columns === 4 ? "md:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-2 lg:grid-cols-3";
  const cardStyle = props.card_style || "elevated";

  return (
    <section className="my-12" data-testid="team-block">
      {(props.title || props.subtitle) && (
        <div className="text-center mb-10 max-w-2xl mx-auto">
          {props.title && <h2 style={{ fontFamily: `'${headingFont}', serif` }} className="text-3xl md:text-5xl leading-tight mb-3">{props.title}</h2>}
          {props.subtitle && <p className="text-base opacity-80" style={{ fontFamily: `'${bodyFont}', sans-serif` }}>{props.subtitle}</p>}
        </div>
      )}
      <div className={`grid grid-cols-1 ${cols} gap-6`}>
        {list.map((m) => (
          <TeamCard key={m.id} member={m} theme={theme} primary={primary} cardSurface={cardSurface} blockRadius={blockRadius} headingFont={headingFont} bodyFont={bodyFont} showBio={props.show_bio} showContact={props.show_contact} showSocial={props.show_social} variant={cardStyle} />
        ))}
      </div>
    </section>
  );
}

function TeamCard({ member, theme, primary, cardSurface, blockRadius, headingFont, bodyFont, showBio, showContact, showSocial, variant }) {
  const photo = member.photo_url ? fileUrl(member.photo_url) : null;
  const initial = (member.name || "?").slice(0, 1).toUpperCase();

  const socials = [
    { url: member.instagram_url, Icon: Instagram, label: "Instagram" },
    { url: member.facebook_url, Icon: Facebook, label: "Facebook" },
    { url: member.tiktok_url, Icon: TikTokIcon, label: "TikTok" },
    { url: member.linkedin_url, Icon: Linkedin, label: "LinkedIn" },
    { url: member.website_url, Icon: Globe, label: "Website" },
  ].filter((s) => s.url);

  if (variant === "overlay") {
    return (
      <div className="relative overflow-hidden group" style={{ ...cardSurface, padding: 0, aspectRatio: "3 / 4" }} data-testid={`team-card-${member.id}`}>
        {photo ? (
          <img src={photo} alt={member.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-6xl" style={{ background: primary, color: "#fff", fontFamily: `'${headingFont}', serif` }}>{initial}</div>
        )}
        <div className="absolute inset-x-0 bottom-0 p-5 text-white" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 100%)" }}>
          <p style={{ fontFamily: `'${headingFont}', serif` }} className="text-2xl leading-tight">{member.name}</p>
          {member.role && <p className="text-xs uppercase tracking-wider opacity-85 mt-1">{member.role}</p>}
          {showBio && member.bio && <p className="text-xs mt-2 opacity-90 line-clamp-3" style={{ fontFamily: `'${bodyFont}', sans-serif` }}>{member.bio}</p>}
          {(showContact || showSocial) && (
            <div className="flex flex-wrap gap-2 mt-3">
              {showSocial && socials.map((s, i) => {
                const Ico = s.Icon;
                return (
                  <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="w-7 h-7 rounded-full flex items-center justify-center backdrop-blur" style={{ background: "rgba(255,255,255,0.18)" }}>
                    <Ico className="w-3.5 h-3.5" strokeWidth={1.6} />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <article className="overflow-hidden flex flex-col" style={cardSurface} data-testid={`team-card-${member.id}`}>
      <div className="aspect-[4/5] relative overflow-hidden" style={{ borderTopLeftRadius: "inherit", borderTopRightRadius: "inherit" }}>
        {photo ? (
          <img src={photo} alt={member.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: `${primary}15`, color: primary, fontFamily: `'${headingFont}', serif`, fontSize: 72 }}>{initial}</div>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <p style={{ fontFamily: `'${headingFont}', serif` }} className="text-2xl leading-tight">{member.name}</p>
        {member.role && <p className="text-xs uppercase tracking-wider mt-1.5" style={{ color: primary, letterSpacing: "0.12em" }}>{member.role}</p>}
        {showBio && member.bio && (
          <p className="text-sm leading-relaxed mt-3 opacity-80 flex-1" style={{ fontFamily: `'${bodyFont}', sans-serif` }}>{member.bio}</p>
        )}
        {(showContact && (member.phone || member.email)) && (
          <div className="mt-4 space-y-1.5 text-sm">
            {member.phone && (
              <a href={`tel:${member.phone}`} className="flex items-center gap-2 hover:opacity-70 transition-opacity">
                <Phone className="w-3.5 h-3.5" strokeWidth={1.6} style={{ color: primary }} /> <span>{member.phone}</span>
              </a>
            )}
            {member.email && (
              <a href={`mailto:${member.email}`} className="flex items-center gap-2 hover:opacity-70 transition-opacity break-all">
                <Mail className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.6} style={{ color: primary }} /> <span>{member.email}</span>
              </a>
            )}
          </div>
        )}
        {showSocial && socials.length > 0 && (
          <div className="flex gap-2 mt-4 pt-4 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
            {socials.map((s, i) => {
              const Ico = s.Icon;
              return (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="w-9 h-9 rounded-full flex items-center justify-center transition-colors hover:opacity-80" style={{ background: `${primary}15`, color: primary }}>
                  <Ico className="w-4 h-4" strokeWidth={1.6} />
                </a>
              );
            })}
          </div>
        )}
      </div>
    </article>
  );
}

// Lucide doesn't ship TikTok — simple inline SVG to match stroke style.
function TikTokIcon({ className, strokeWidth = 1.6 }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
  );
}

function PublicForm({ form, primary, buttonRadius, cardSurface, onSubmit }) {
  const [values, setValues] = useState({});
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (onSubmit) {
      const ok = await onSubmit(form.id, values);
      if (ok) setDone(true);
    }
  };
  if (done) return <p className="my-6 p-5 text-center" style={{ ...cardSurface, background: "var(--mv-surface)" }}>{form.success_message || "Vielen Dank!"}</p>;
  return (
    <form onSubmit={submit} className="my-6 max-w-2xl mx-auto space-y-4 p-6" style={cardSurface}>
      {(form.fields || []).map((f) => (
        <div key={f.id}>
          <label className="block text-sm font-medium mb-1.5">{f.label}{f.required && " *"}</label>
          {f.type === "textarea" ? (
            <textarea required={f.required} className="mv-input min-h-[120px]" onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} />
          ) : (
            <input required={f.required} type={f.type} className="mv-input" onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} />
          )}
        </div>
      ))}
      <button className="px-6 py-2.5 text-white font-medium" style={{ background: primary, borderRadius: buttonRadius }}>Senden</button>
    </form>
  );
}


function BookingBlock({ title, subtitle, headingFont, primary, services, salonSlug, cardSurface, buttonRadius, blockRadius }) {
  return (
    <section className="my-10 max-w-3xl mx-auto p-6 md:p-10" style={cardSurface}>
      <p style={{ fontFamily: `'${headingFont}', serif` }} className="text-3xl mb-1">{title}</p>
      <p className="text-sm mb-6 opacity-75">{subtitle}</p>

      {(services || []).length > 0 && (
        <div className="grid md:grid-cols-2 gap-3 mb-6">
          {(services || []).slice(0, 4).map((s) => (
            <div key={s.id} className="p-4 border" style={{ borderColor: "var(--mv-border)", borderRadius: blockRadius }}>
              <p className="font-medium">{s.name}</p>
              <p className="text-xs opacity-75">{s.duration_minutes} Min · {s.price?.toFixed(2)} €</p>
            </div>
          ))}
        </div>
      )}

      {salonSlug ? (
        <a href={`/salon/${salonSlug}/booking`} data-testid="booking-cta" className="inline-flex items-center px-7 py-3.5 text-white font-medium" style={{ background: primary, borderRadius: buttonRadius }}>
          Termin online buchen
        </a>
      ) : (
        <span className="inline-flex items-center px-7 py-3.5 text-white font-medium opacity-60" style={{ background: primary, borderRadius: buttonRadius }}>
          Termin online buchen (Vorschau)
        </span>
      )}
    </section>
  );
}

function NewsletterBlock({ title, subtitle, headingFont, primary, salonSlug, blockRadius, buttonRadius }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!salonSlug) { setDone("Vorschau-Modus."); return; }
    try {
      const { default: apiClient } = await import("@/lib/api");
      const { data } = await apiClient.post(`/public/salon/${salonSlug}/newsletter`, { email, name });
      setDone(data.message || "Erfolgreich!");
    } catch { setDone("Fehler bei der Anmeldung."); }
  };

  return (
    <section className="my-10 p-8 md:p-12" style={{ background: "var(--mv-surface)", borderRadius: blockRadius }}>
      <div className="max-w-2xl mx-auto text-center">
        <p style={{ fontFamily: `'${headingFont}', serif` }} className="text-3xl md:text-4xl mb-2">{title}</p>
        <p className="text-sm md:text-base opacity-75 mb-6">{subtitle}</p>
        {done ? <p className="font-medium">{done}</p> : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input type="email" required placeholder="Ihre E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} className="mv-input flex-1" data-testid="newsletter-email-input" />
            <button data-testid="newsletter-submit" className="px-6 py-3 text-white font-medium" style={{ background: primary, borderRadius: buttonRadius }}>Anmelden</button>
          </form>
        )}
      </div>
    </section>
  );
}


function LoyaltyBlock({ title, subtitle, ctaLabel, maxRewards = 3, headingFont, primary, salonSlug, loyalty, blockRadius, buttonRadius }) {
  const enabled = loyalty?.settings?.enabled;
  const rewards = (loyalty?.rewards || []).slice(0, maxRewards);
  const pointsPerEuro = loyalty?.settings?.points_per_euro || 1;
  const welcome = loyalty?.settings?.welcome_bonus || 0;
  const birthday = loyalty?.settings?.birthday_bonus || 0;

  if (!enabled) return null;

  return (
    <section
      className="my-12 p-8 md:p-12 text-white overflow-hidden relative"
      style={{ background: `linear-gradient(135deg, ${primary}, ${primary}dd)`, borderRadius: blockRadius }}
      data-testid="loyalty-block"
    >
      <div className="max-w-4xl mx-auto relative z-10">
        <p className="text-xs uppercase tracking-wider opacity-80 mb-2">Treueprogramm</p>
        <h2 style={{ fontFamily: `'${headingFont}', serif` }} className="text-3xl md:text-5xl leading-tight mb-3">{title}</h2>
        <p className="text-base md:text-lg opacity-90 mb-8 max-w-2xl">{subtitle}</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
          <div className="p-4 rounded-2xl backdrop-blur-sm" style={{ background: "rgba(255,255,255,0.15)" }}>
            <p className="text-3xl font-display">{pointsPerEuro}×</p>
            <p className="text-sm opacity-90 mt-1">Punkte pro € Umsatz</p>
          </div>
          {welcome > 0 && (
            <div className="p-4 rounded-2xl backdrop-blur-sm" style={{ background: "rgba(255,255,255,0.15)" }}>
              <p className="text-3xl font-display">+{welcome}</p>
              <p className="text-sm opacity-90 mt-1">Willkommens-Bonus</p>
            </div>
          )}
          {birthday > 0 && (
            <div className="p-4 rounded-2xl backdrop-blur-sm" style={{ background: "rgba(255,255,255,0.15)" }}>
              <p className="text-3xl font-display">🎂 +{birthday}</p>
              <p className="text-sm opacity-90 mt-1">Geburtstags-Bonus</p>
            </div>
          )}
        </div>

        {rewards.length > 0 && (
          <div className="mb-8">
            <p className="text-sm uppercase tracking-wider opacity-80 mb-3">Belohnungen</p>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {rewards.map((r) => (
                <div key={r.id} className="p-4 rounded-2xl text-left" style={{ background: "rgba(255,255,255,0.95)", color: "#1a1a1a" }} data-testid={`loyalty-block-reward-${r.id}`}>
                  <p className="font-medium text-sm mb-1">{r.name}</p>
                  <p className="text-xs opacity-75">
                    {r.reward_type === "discount_percent" && `${r.reward_value}% Rabatt`}
                    {r.reward_type === "discount_fixed" && `${r.reward_value}€ Rabatt`}
                    {r.reward_type === "free_service" && "Gratis-Leistung"}
                  </p>
                  <p className="text-xs mt-2 font-medium" style={{ color: primary }}>{r.points_cost} Punkte</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {salonSlug ? (
          <a
            href={`/salon/${salonSlug}/account`}
            data-testid="loyalty-block-cta"
            className="inline-flex items-center px-7 py-3.5 font-medium"
            style={{ background: "#fff", color: primary, borderRadius: buttonRadius }}
          >
            {ctaLabel}
          </a>
        ) : (
          <span className="inline-flex items-center px-7 py-3.5 font-medium opacity-70" style={{ background: "#fff", color: primary, borderRadius: buttonRadius }}>
            {ctaLabel} (Vorschau)
          </span>
        )}
      </div>
    </section>
  );
}


function formatPrice(p) {
  const n = typeof p === "number" ? p : parseFloat(p || 0);
  return `${n.toFixed(2).replace(".", ",")} €`;
}

function PricelistBlock({ props, services, addons, categories, headingFont, bodyFont, primary, salonSlug, cardSurface, blockRadius, buttonRadius, bookingAllowed = true }) {
  const activeServices = (services || []).filter((s) => s.active !== false);
  const wantedCatIds = props.category_ids && props.category_ids.length > 0 ? new Set(props.category_ids) : null;

  // Group services by category_id
  const cats = (categories || []).filter((c) => c.active !== false).sort((a, b) => (a.order || 0) - (b.order || 0));
  const grouped = props.group_by_category !== false
    ? cats
        .filter((c) => !wantedCatIds || wantedCatIds.has(c.id))
        .map((c) => ({
          category: c,
          items: activeServices.filter((s) => s.category_id === c.id),
        }))
        .filter((g) => g.items.length > 0)
    : [];

  // Uncategorized bucket (only if grouping is on)
  if (props.group_by_category !== false && props.show_uncategorized !== false && !wantedCatIds) {
    const uncatItems = activeServices.filter((s) => !s.category_id || !cats.find((c) => c.id === s.category_id));
    if (uncatItems.length > 0) {
      grouped.push({ category: { id: "__uncat__", name: "Weitere Leistungen" }, items: uncatItems });
    }
  }

  // Flat list when grouping is off
  const flat = props.group_by_category === false
    ? (wantedCatIds
        ? activeServices.filter((s) => s.category_id && wantedCatIds.has(s.category_id))
        : activeServices)
    : [];

  const renderItem = (s) => {
    const svcAddons = (addons || []).filter((a) => a.service_id === s.id && a.active !== false);
    return (
      <div
        key={s.id}
        className="py-4 border-b last:border-b-0"
        style={{ borderColor: "rgba(0,0,0,0.08)" }}
        data-testid={`pricelist-item-${s.id}`}
      >
        <div className="flex justify-between gap-4 items-baseline">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-base md:text-lg" style={{ fontFamily: `'${bodyFont}', sans-serif` }}>{s.name}</p>
            {props.show_description !== false && s.description && (
              <p className="text-sm opacity-70 mt-1">{s.description}</p>
            )}
            {props.show_duration !== false && s.duration_minutes ? (
              <p className="text-xs opacity-60 mt-1">{s.duration_minutes} Min</p>
            ) : null}
          </div>
          <div className="text-right shrink-0">
            <p className="font-medium text-base md:text-lg whitespace-nowrap" style={{ color: primary }}>
              {formatPrice(s.price)}
            </p>
          </div>
        </div>
        {props.show_addons !== false && svcAddons.length > 0 && (
          <div className="mt-3 pl-4 border-l-2 space-y-1.5" style={{ borderColor: `${primary}40` }}>
            <p className="text-[11px] uppercase tracking-wider opacity-60">Zusatzleistungen</p>
            {svcAddons.map((a) => (
              <div key={a.id} className="flex justify-between gap-3 text-sm" data-testid={`pricelist-addon-${a.id}`}>
                <div className="min-w-0">
                  <span>{a.name}</span>
                  {a.duration_minutes ? <span className="opacity-50 text-xs ml-2">+{a.duration_minutes} Min</span> : null}
                </div>
                <span className="opacity-80 whitespace-nowrap">+ {formatPrice(a.price)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (g) => (
    <div key={g.category.id} className="mb-10 last:mb-0" data-testid={`pricelist-group-${g.category.id}`}>
      <h3
        className="text-xl md:text-2xl mb-3 pb-2 border-b"
        style={{
          fontFamily: `'${headingFont}', serif`,
          borderColor: `${primary}40`,
          color: primary,
        }}
      >
        {g.category.name}
      </h3>
      {props.layout === "cards" ? (
        <div className="grid md:grid-cols-2 gap-4">
          {g.items.map((s) => (
            <div key={s.id} className="p-4" style={cardSurface}>
              {renderItem(s)}
            </div>
          ))}
        </div>
      ) : (
        <div>{g.items.map(renderItem)}</div>
      )}
    </div>
  );

  const isEmpty = (props.group_by_category === false ? flat.length : grouped.length) === 0;

  return (
    <section className="my-12" data-testid="pricelist-block">
      {(props.title || props.subtitle) && (
        <div className="text-center mb-8 max-w-2xl mx-auto">
          {props.title && (
            <h2 style={{ fontFamily: `'${headingFont}', serif` }} className="text-3xl md:text-5xl leading-tight mb-3">
              {props.title}
            </h2>
          )}
          {props.subtitle && <p className="text-base opacity-75">{props.subtitle}</p>}
        </div>
      )}

      {isEmpty ? (
        <p className="text-center text-sm italic opacity-60 py-8">
          Noch keine Leistungen vorhanden — bitte zuerst unter &bdquo;Leistungen&ldquo; anlegen.
        </p>
      ) : props.group_by_category === false ? (
        props.layout === "cards" ? (
          <div className="grid md:grid-cols-2 gap-4">
            {flat.map((s) => (
              <div key={s.id} className="p-4" style={cardSurface}>
                {renderItem(s)}
              </div>
            ))}
          </div>
        ) : (
          <div>{flat.map(renderItem)}</div>
        )
      ) : (
        grouped.map(renderGroup)
      )}

      {props.show_cta !== false && props.cta_label && !isEmpty && bookingAllowed && (
        <div className="text-center mt-10">
          {salonSlug ? (
            <a
              href={`/salon/${salonSlug}/booking`}
              data-testid="pricelist-cta"
              className="inline-flex items-center px-7 py-3.5 text-white font-medium"
              style={{ background: primary, borderRadius: buttonRadius }}
            >
              {props.cta_label}
            </a>
          ) : (
            <span
              className="inline-flex items-center px-7 py-3.5 text-white font-medium opacity-70"
              style={{ background: primary, borderRadius: buttonRadius }}
            >
              {props.cta_label} (Vorschau)
            </span>
          )}
        </div>
      )}
    </section>
  );
}
